import {
    Alert,
    Box,
    Button,
    Chip,
    DialogContent,
    Paper,
    Rating,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useEffect, useRef, useState } from "react";
import PositionClient, { PositionErrors } from "../apiClients/PositionClient";
import { Position } from '../models/dtos';
import { useErrorDisplay } from "../components/ErrorDisplay";
import { GetUpsertIcon, IsEmpty } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { UpsertAction } from "../Enums";
import AiTemplateClient, { AiChatMessage, AiContextualAiAnswer, AiFeedbackOutcome } from "../apiClients/AiTemplateClient";
import useViewportMode from "../hooks/useViewportMode";
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";
import ContextualAiDialog from "../components/ai/ContextualAiDialog";
import ContextualAiWorkspace from "../components/ai/ContextualAiWorkspace";
import { ContextualAiWorkflowAdapter } from "../components/ai/contextualAiTypes";
import { rememberContextSummary } from "../components/ai/ContextualAiContextMemory";
import { buildContextualRequest } from "../components/ai/contextualAiWire";
import { I18N } from "../I18N";

export interface PositionUpsertProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    positionId?: string;
    inPosition?: Position;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    icon?: JSX.Element;
}

const INITIAL_ASSISTANT_MESSAGE = "Here's your starting job description. What would you like me to tweak?";
const BLANK_DESCRIPTION_PROMPT = 'Create an initial job description based on the position title.';

const normalizeConversationMessages = (messages: unknown): AiChatMessage[] => {
    if (!Array.isArray(messages))
        return [];

    return messages.flatMap((message) => {
        if (message === null || typeof message !== 'object')
            return [];

        const serialized = message as {
            role?: string;
            content?: string;
            Role?: string;
            Content?: string;
        };
        const role = (serialized.role ?? serialized.Role)?.toLowerCase();
        const content = serialized.content ?? serialized.Content;

        if ((role !== 'user' && role !== 'assistant') || typeof content !== 'string' || IsEmpty(content))
            return [];

        return [{ role, content } as AiChatMessage];
    });
};

const PositionUpsertPopup = ({ mode, positionId, inPosition, onChange = async () => { }, onAdd = async () => { }, icon = GetUpsertIcon(mode), ...btnProps }: PositionUpsertProps) => {
    const [ErrorDisplay, setError, clearError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const viewport = useViewportMode();
    const singlePane = viewport.isPhone || (viewport.isTablet && viewport.isPortrait);
    const [position, setPosition] = useState<Position>(new Position());
    const [wizardStep, setWizardStep] = useState<1 | 2>(mode === UpsertAction.Create ? 1 : 2);
    const [activitySummary, setActivitySummary] = useState<string>('');
    const [aiBusy, setAiBusy] = useState<boolean>(false);
    const [descriptionMessages, setDescriptionMessages] = useState<AiChatMessage[]>([]);
    const [descriptionInteractionId, setDescriptionInteractionId] = useState<string>('');
    const [aiRating, setAiRating] = useState<number | null>(null);
    const [aiFeedback, setAiFeedback] = useState<string>('');
    const [contextSummary, setContextSummary] = useState<string>('');
    const [contextAnswers, setContextAnswers] = useState<AiContextualAiAnswer[]>([]);
    const [aiDialogOpen, setAiDialogOpen] = useState<boolean>(false);
    const [openAiAfterGeneration, setOpenAiAfterGeneration] = useState<boolean>(false);
    const [initialGenerationFailed, setInitialGenerationFailed] = useState<boolean>(false);
    const positionWorkspaceRef = useRef<HTMLDivElement | null>(null);
    const aiDialogOpenerRef = useRef<HTMLButtonElement | null>(null);
    const loadGenerationRef = useRef(0);

    const disableNext = IsEmpty(position?.Title) || IsEmpty(activitySummary);
    const disableSave = IsEmpty(position?.Title) || IsEmpty(position?.Description);
    const title = mode === UpsertAction.Create ? 'Create Position' : 'Edit Position';

    const resetAiFeedback = () => {
        setDescriptionInteractionId('');
        setAiRating(null);
        setAiFeedback('');
    };

    const hydrateConversation = async (editablePosition: Position, generation: number) => {
        try {
            const conversation = await new AiTemplateClient().GetPositionDescriptionConversation(positionId);
            if (loadGenerationRef.current !== generation)
                return;

            let storedMessages = normalizeConversationMessages(conversation.Messages);
            let interactionId = conversation.InteractionId ?? '';
            let loadedContextSummary = conversation.ContextSummary ?? '';

            if (IsEmpty(editablePosition.Description) && !IsEmpty(editablePosition.Title)) {
                const initialMessages: AiChatMessage[] = [
                    ...storedMessages,
                    { role: 'user', content: BLANK_DESCRIPTION_PROMPT }
                ];

                try {
                    setAiBusy(true);
                    const response = await new AiTemplateClient().GeneratePositionDescription({
                        PositionTitle: editablePosition.Title.trim(),
                        CurrentDescription: '',
                        Messages: initialMessages,
                        PositionId: positionId || undefined,
                        InteractionId: interactionId || undefined,
                        RoleContextSummary: loadedContextSummary || undefined,
                        ContextAnswers: [],
                    });
                    if (loadGenerationRef.current !== generation)
                        return;

                    setPosition(current => ({ ...current, Description: response.UpdatedDescription }));
                    storedMessages = [
                        ...initialMessages,
                        { role: 'assistant', content: response.AssistantMessage || INITIAL_ASSISTANT_MESSAGE }
                    ];
                    interactionId = response.InteractionId;
                    loadedContextSummary = response.ContextSummary ?? loadedContextSummary;
                } catch (error) {
                    if (loadGenerationRef.current === generation)
                        setError(error, 'Unable to generate the initial job description');
                } finally {
                    if (loadGenerationRef.current === generation)
                        setAiBusy(false);
                }
            }

            if (loadGenerationRef.current !== generation)
                return;
            setDescriptionMessages(storedMessages.length > 0
                ? storedMessages
                : [{ role: 'assistant', content: INITIAL_ASSISTANT_MESSAGE }]);
            setDescriptionInteractionId(interactionId);
            setContextSummary(loadedContextSummary);
        } catch (error) {
            // Conversation history enriches the editor but must never hold the position form hostage.
            // Keep editing available and surface a recoverable history error only in the AI path.
            console.warn('Unable to load position AI conversation history.', error);
        }
    };

    const loadData = async () => {
        const generation = ++loadGenerationRef.current;
        try {
            setBusy(true);
            if (mode === UpsertAction.Create) {
                setPosition(new Position());
                setWizardStep(1);
                setInitialGenerationFailed(false);
                setActivitySummary('');
                setDescriptionMessages([]);
                setContextSummary('');
                setContextAnswers([]);
                resetAiFeedback();
                return;
            }

            const loadedPosition = inPosition
                ? inPosition
                : await new PositionClient().GetSingle(positionId);
            if (loadGenerationRef.current !== generation)
                return;
            const editablePosition = { ...loadedPosition };
            setPosition(editablePosition);
            setWizardStep(2);
            setDescriptionMessages([{ role: 'assistant', content: INITIAL_ASSISTANT_MESSAGE }]);
            setDescriptionInteractionId('');
            setContextSummary('');
            setContextAnswers([]);
            setAiRating(null);
            setAiFeedback('');
            // History is useful context, but it is not required to render or edit the position.
            // Load it after the form becomes interactive instead of blocking the dialog.
            void hydrateConversation(editablePosition, generation);
        } catch (error) {
            setError(error, PositionErrors.GetSingle);
        } finally {
            setBusy(false);
        }
    };

    const handleOpen = async () => {
        setOpenAiAfterGeneration(false);
        setAiDialogOpen(false);
        setOpen(true);
        await loadData();
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setPosition(current => ({ ...current, [name]: value }));
    };

    const generateInitialDescription = async () => {
        if (disableNext)
            return;

        const initialMessages: AiChatMessage[] = [{
            role: 'user',
            content: `The primary activities and responsibilities for this position are: ${activitySummary.trim()}`
        }];
        const initialAnswers: AiContextualAiAnswer[] = [{
            GapKey: 'RoleOutcomesAndFailureCosts',
            Answer: activitySummary.trim(),
        }];

        try {
            clearError();
            setInitialGenerationFailed(false);
            setAiBusy(true);
            const response = await new AiTemplateClient().GeneratePositionDescription({
                PositionTitle: position.Title.trim(),
                CurrentDescription: '',
                Messages: initialMessages,
                InteractionId: descriptionInteractionId || undefined,
                RoleContextSummary: activitySummary.trim(),
                ContextAnswers: initialAnswers,
            });

            setPosition(current => ({ ...current, Description: response.UpdatedDescription }));
            setDescriptionMessages([
                ...initialMessages,
                {
                    role: 'assistant',
                    content: response.AssistantMessage
                        ? `${response.AssistantMessage} ${INITIAL_ASSISTANT_MESSAGE}`
                        : INITIAL_ASSISTANT_MESSAGE
                }
            ]);
            setDescriptionInteractionId(response.InteractionId);
            setContextSummary(response.ContextSummary ?? '');
            setContextAnswers(initialAnswers);
            setWizardStep(2);
            setOpenAiAfterGeneration(true);
        } catch (error) {
            setInitialGenerationFailed(true);
            setError(error, 'Unable to generate the initial job description');
        } finally {
            setAiBusy(false);
        }
    };

    const writeDescriptionManually = () => {
        clearError();
        setInitialGenerationFailed(false);
        setOpenAiAfterGeneration(false);
        setAiDialogOpen(false);
        setDescriptionMessages([]);
        setDescriptionInteractionId('');
        setContextSummary('');
        setContextAnswers([]);
        setWizardStep(2);
    };

    useEffect(() => {
        if (!openAiAfterGeneration || wizardStep !== 2 || !positionWorkspaceRef.current || !aiDialogOpenerRef.current)
            return;
        aiDialogOpenerRef.current.focus();
        setAiDialogOpen(true);
        setOpenAiAfterGeneration(false);
    }, [openAiAfterGeneration, wizardStep]);

    const recordDescriptionFeedback = async (outcome: AiFeedbackOutcome, savedPositionId?: string) => {
        if (IsEmpty(descriptionInteractionId))
            return;

        try {
            await new AiTemplateClient().RecordFeedback({
                InteractionId: descriptionInteractionId,
                PositionId: savedPositionId || undefined,
                Workflow: 'PositionDescription',
                Outcome: outcome,
                Rating: aiRating,
                Feedback: aiFeedback,
                FinalPositionDescription: position.Description
            });
        } catch (error) {
            console.warn('Unable to record AI description feedback.', error);
        }
    };

    const handleSubmit = async () => {
        try {
            setBusy(true);
            let savedPositionId = positionId;

            if (mode === UpsertAction.Create) {
                savedPositionId = await new PositionClient().Create({
                    Title: position.Title,
                    Description: position.Description
                });
                await onAdd(savedPositionId);
            } else {
                await new PositionClient().Update(positionId, {
                    Title: position.Title,
                    Description: position.Description
                });
                await onChange();
            }

            await recordDescriptionFeedback('Accepted', savedPositionId);
            loadGenerationRef.current += 1;
            setAiDialogOpen(false);
            setOpen(false);
        } catch (error) {
            setError(error, mode === UpsertAction.Create ? PositionErrors.Create : PositionErrors.Update);
        } finally {
            setBusy(false);
        }
    };

    const handleCancel = async () => {
        try {
            setAiBusy(true);
            await recordDescriptionFeedback('Dismissed', positionId);
        } finally {
            loadGenerationRef.current += 1;
            setAiBusy(false);
            setAiDialogOpen(false);
            setOpen(false);
        }
    };

    const renderStepOne = () => (
        <Box sx={{ width: { xs: '100%', sm: 720 }, maxWidth: '100%', mx: 'auto', py: { xs: 1, sm: 3 } }}>
            <Stack spacing={3}>
                <Box>
                    <Chip
                        icon={<AutoAwesomeOutlinedIcon />}
                        label="Step 1 of 2 · Position brief"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 2 }}
                    />
                    <Typography variant="h3" gutterBottom>Tell us about the role</Typography>
                    <Typography color="text.secondary">
                        Start with the essentials. Synergist AI will combine this brief with your organization's skills and position guidance to draft a complete job description.
                    </Typography>
                </Box>
                <TextField
                    label="Position title"
                    fullWidth
                    autoFocus
                    required
                    name="Title"
                    value={position.Title}
                    inputProps={{ maxLength: 50 }}
                    helperText={`${position.Title.length}/50 characters`}
                    onChange={handleInputChange}
                />
                <TextField
                    label="Brief activity summary"
                    fullWidth
                    required
                    multiline
                    minRows={6}
                    value={activitySummary}
                    inputProps={{ maxLength: 2000 }}
                    helperText="Describe the work this person will do, their key responsibilities, and the outcomes they should own. Plain language is fine."
                    onChange={(event) => setActivitySummary(event.target.value)}
                />
                <Alert severity="info" icon={<AutoAwesomeOutlinedIcon fontSize="inherit" />}>
                    When you select Next, AI will generate a starting job description. You can edit every word and continue refining it in chat before saving.
                </Alert>
                {initialGenerationFailed && (
                    <Alert severity="warning" data-testid="initial-position-generation-recovery">
                        {I18N.Translate('AI could not generate a starting description. You can retry or continue and write it yourself.')}
                    </Alert>
                )}
            </Stack>
        </Box>
    );

    const renderConversation = () => {
        const contextKey = `position:${positionId || position.Title.trim().toLowerCase()}`;
        const adapter: ContextualAiWorkflowAdapter<string> = {
            workflow: 'position-description',
            sessionKey: contextKey,
            voiceWorkflow: 'PositionDescription',
            contextId: positionId || null,
            capabilities: { freeText: true, voice: true, editableContextSummary: true },
            initialMessages: descriptionMessages.filter(message => message.role !== 'system').map(message => ({ role: message.role as 'user' | 'assistant', content: message.content })),
            initialContextSummary: contextSummary,
            progressLabel: 'Updating description',
            submit: async request => {
                const response = await new AiTemplateClient().ContinueContextualChat(buildContextualRequest('position-description', 'generate-now', {
                    PositionTitle: position.Title,
                    JobDescription: position.Description ?? '',
                    Answers: contextAnswers,
                    Messages: request.messages.map(({ role, content }) => ({ role, content })),
                    PositionId: positionId || undefined,
                    InteractionId: descriptionInteractionId || undefined,
                    ContextSummary: request.contextSummary || contextSummary,
                    GenerateNow: true,
                }));
                if (!response.PositionDescription)
                    return {
                        assistantMessage: response.Rationale,
                        contextSummary: response.ContextSummary,
                        question: response.QuestionText,
                        choices: (response.SuggestedChoices ?? []).map((label, index) => ({ id: `${response.GapKey ?? 'choice'}-${index}`, label, value: label })),
                        allowFreeText: response.AllowFreeText,
                        canSkip: response.AllowSkip,
                        canGenerateNow: response.AllowGenerateNow,
                    };
                const description = response.PositionDescription;
                setDescriptionInteractionId(response.InteractionId);
                setContextSummary(response.ContextSummary ?? request.contextSummary);
                setPosition(current => ({ ...current, Description: description.UpdatedDescription }));
                rememberContextSummary(contextKey, response.ContextSummary ?? request.contextSummary);
                return { assistantMessage: description.AssistantMessage, contextSummary: response.ContextSummary, result: description.UpdatedDescription };
            },
        };
        return (
            <ContextualAiWorkspace
                adapter={adapter}
                testId="position-ai-workspace"
                transcriptTestId="position-ai-transcript"
                composerTestId="position-ai-composer"
                composerLabel="Message Synergist AI"
                onMessagesChange={messages => setDescriptionMessages(messages)}
                onContextSummaryChange={next => { setContextSummary(next); rememberContextSummary(contextKey, next); }}
                footer={!IsEmpty(descriptionInteractionId) ? (
                    <Stack spacing={1} sx={{ flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center"><Typography variant="caption" color="text.secondary">Helpful?</Typography><Rating size="small" value={aiRating} onChange={(_, value) => setAiRating(value)} /></Stack>
                        {aiRating !== null && <TextField fullWidth size="small" label="Optional feedback" value={aiFeedback} onChange={event => setAiFeedback(event.target.value)} inputProps={{ maxLength: 2000 }} />}
                    </Stack>
                ) : undefined}
            />
        );
    };

    const renderPositionDetails = () => (
        <Paper
            data-testid="position-details-workspace"
            variant="outlined"
            sx={{
                p: { xs: 2, sm: 3 },
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: '100%',
                overflowY: singlePane ? 'auto' : 'hidden',
            }}
        >
            <Stack spacing={2} sx={{ height: singlePane ? 'auto' : '100%', minHeight: 0 }}>
                <Box>
                    <Typography variant="overline" color="primary.main">Position details</Typography>
                    <Typography variant="h4">Shape the final description</Typography>
                </Box>
                <TextField
                    label="Position title"
                    fullWidth
                    required
                    name="Title"
                    value={position.Title}
                    inputProps={{ maxLength: 50 }}
                    onChange={handleInputChange}
                />
                <TextField
                    label="Job description"
                    fullWidth
                    required
                    name="Description"
                    value={position.Description}
                    multiline
                    minRows={singlePane ? 12 : undefined}
                    onChange={handleInputChange}
                    sx={singlePane ? undefined : {
                        flex: 1,
                        minHeight: 0,
                        '& .MuiInputBase-root': {
                            height: '100%',
                            alignItems: 'flex-start'
                        },
                        '& textarea:not([aria-hidden="true"])': {
                            height: '100% !important',
                            overflowY: 'auto !important',
                            boxSizing: 'border-box'
                        }
                    }}
                />
            </Stack>
        </Paper>
    );

    const renderStepTwo = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: { xs: '100%', sm: 720 }, maxWidth: '100%' }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 2, flexShrink: 0 }}
            >
                {mode === UpsertAction.Create && (
                    <Chip label="Step 2 of 2 · Refine and save" color="primary" variant="outlined" />
                )}
                <Typography variant="body2" color="text.secondary">
                    Edit directly or ask the assistant for another pass.
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    ref={aiDialogOpenerRef}
                    variant="outlined"
                    startIcon={<AutoAwesomeOutlinedIcon />}
                    onClick={() => setAiDialogOpen(true)}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'center' } }}
                >
                    Open Synergist AI
                </Button>
            </Stack>
            <Box
                ref={positionWorkspaceRef}
                data-testid="position-workspace-layout"
                data-layout="details-only"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {renderPositionDetails()}
            </Box>
            <ContextualAiDialog
                open={aiDialogOpen}
                onClose={() => {
                    setAiDialogOpen(false);
                }}
                anchorEl={positionWorkspaceRef.current}
                title="Synergist AI — Position Description"
                workflow="position-generation"
            >
                {renderConversation()}
            </ContextualAiDialog>
        </Box>
    );

    return <>
        <ErrorDisplay displayType={initialGenerationFailed ? 'message' : 'dialog'} />
        <IconButtonEx disabled={busy} hoverContent={title} size='small' onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog
            aria-labelledby="position-editor-title"
            open={open}
            fullWidth={false}
            maxWidth="md"
            widthTier="compact"
            tabletFullScreen="portrait"
            PaperProps={{
                sx: {
                    height: 'calc(100dvh - 48px)',
                    maxHeight: 900,
                    overflow: 'hidden'
                }
            }}
        >
            <BusySpinner busy={busy || (aiBusy && !aiDialogOpen)}>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                    <ResponsiveDialogHeader
                        id="position-editor-title"
                        title={title}
                        sx={{ m: 0, px: viewport.isPhone ? 2 : 3, py: 2 }}
                    />
                    <DialogContent
                        dividers
                        sx={{
                            p: { xs: 2, sm: 3 },
                            bgcolor: 'background.default',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            overflow: wizardStep === 1 ? 'auto' : 'hidden'
                        }}
                    >
                        {wizardStep === 1 ? renderStepOne() : renderStepTwo()}
                    </DialogContent>
                    <StickyDialogActions
                        data-testid="position-editor-actions"
                        phoneLayout="stacked"
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 1.5,
                            flexShrink: 0,
                            bgcolor: 'background.paper',
                            borderTop: 1,
                            borderColor: 'divider'
                        }}
                    >
                        <Button onClick={handleCancel} variant="text" color="inherit">Cancel</Button>
                        {!singlePane && <Box sx={{ flexGrow: 1 }} />}
                        {mode === UpsertAction.Create && wizardStep === 2 && (
                            <Button disabled={aiBusy} onClick={() => { setAiDialogOpen(false); setWizardStep(1); }} color="inherit">Back</Button>
                        )}
                        {wizardStep === 1 ? (
                            <>
                                <Button
                                    disabled={IsEmpty(position.Title) || aiBusy}
                                    onClick={writeDescriptionManually}
                                    variant="outlined"
                                >
                                    {I18N.Translate('Write manually')}
                                </Button>
                                <Button
                                    disabled={disableNext || aiBusy}
                                    onClick={generateInitialDescription}
                                    variant="contained"
                                    startIcon={<AutoAwesomeOutlinedIcon />}
                                >
                                    {aiBusy
                                        ? 'Generating...'
                                        : I18N.Translate(initialGenerationFailed ? 'Try AI again' : 'Next: Generate description')}
                                </Button>
                            </>
                        ) : (
                            <Button disabled={disableSave || aiBusy} onClick={handleSubmit} variant="contained" color="primary">
                                {mode === UpsertAction.Create ? 'Create position' : 'Save changes'}
                            </Button>
                        )}
                    </StickyDialogActions>
                </Box>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
};

export default PositionUpsertPopup;
